import { FC } from 'react';
import { Document, Page, Text, Image, View, Font } from '@react-pdf/renderer';
import { Api } from 'further-types/annual-tax-report';

import './fonts';
import styles from './styles';
import TaxReliefs from './TaxReliefs';
import LostTaxReliefs from './LostTaxReliefs';
import Interests from './Interests';
import Dividends from './Dividends';
import Gains from './Gains';
import Losses from './Losses';
import logo from './assets/images/logo.png';

Font.registerHyphenationCallback((word) => [word]);

interface TaxReportProps {
  reportComponents: Api.GetReportResponse['components'];
  firmLogoUrl?: string;
}

const TaxReport: FC<TaxReportProps> = ({ reportComponents, firmLogoUrl }) => {
  return (
    <Document title="Annual tax report">
      <Page size="A4" wrap={true} style={styles.pageContainer}>
        <View fixed style={styles.header}>
          {!!firmLogoUrl && (
            <Image
              src={{
                uri: firmLogoUrl,
                method: 'GET',
                headers: { 'Cache-Control': 'no-cache' },
              }}
              style={styles.firmLogo}
            />
          )}
          <Image style={styles.logo} src={logo} />
        </View>
        <View style={styles.pageContent}>
          <Text style={styles.paragraph}>
            Dear {reportComponents.investorName},
          </Text>
          <Text style={styles.paragraph}>
            Please see your summary for the{' '}
            <Text style={styles.bold}>{reportComponents.taxYear}</Text> tax year
            below.
          </Text>
          <Text style={styles.paragraph}>
            This letter summarises all key transactions that have taken place
            {reportComponents.firmName
              ? ` with ${reportComponents.firmName} `
              : ''}
            this tax year.
          </Text>

          <TaxReliefs
            taxReliefs={reportComponents.taxReliefs}
            totals={reportComponents.totalTaxReliefs}
            taxYear={reportComponents.taxYear}
            kiFundsInvestmentsValue={reportComponents.kiFundsInvestmentsValue}
          />

          <LostTaxReliefs
            lostTaxReliefs={reportComponents.lostReliefs}
            totals={reportComponents.totalLostReliefs}
            taxYear={reportComponents.taxYear}
          />

          <Interests
            interests={reportComponents.interest}
            totalAmount={reportComponents.totalInterests}
            taxYear={reportComponents.taxYear}
          />

          <Dividends
            dividends={reportComponents.dividends}
            totalAmount={reportComponents.totalDividends}
            taxYear={reportComponents.taxYear}
          />

          <Gains
            gains={reportComponents.gains}
            totals={reportComponents.totalGains}
            taxYear={reportComponents.taxYear}
          />

          <Losses
            losses={reportComponents.losses}
            totals={reportComponents.totalLosses}
            taxYear={reportComponents.taxYear}
          />

          <View wrap={false}>
            <Text style={styles.heading}>Summary notice</Text>
            <Text style={styles.paragraph}>
              Nothing in this letter is intended to be or should be construed as
              tax advice. If you have any queries about the data contained in
              this letter, please contact your applicable fund manager for more
              information.
            </Text>
          </View>
          <Text style={styles.paragraph}>
            For any personal tax questions, please seek professional advice from
            an accountant or specialist tax adviser. Alternatively, you may wish
            to contact HMRC or your local tax authority.
          </Text>
          <Text style={styles.paragraph}>
            Appended to this letter are all HMRC tax certificates relating to:
          </Text>

          <Text style={styles.list}>
            i) new investments made in the tax year, where those investments
            were eligible for potential UK income tax relief; and
          </Text>
          <Text style={styles.list}>
            ii) past years' investments, where those investments either (a)
            exited or (b) lost their UK S/EIS tax relief status in this tax year
            (if any).
          </Text>
        </View>
        <Text
          fixed
          style={styles.footer}
          render={({ pageNumber }) => `${pageNumber}`}
        />
      </Page>
    </Document>
  );
};

export default TaxReport;
